/* eslint-disable no-nested-ternary */
import { createElement } from '../../utils/dom';
import Component from '../../Component';
import {
	billsData,
	BillData,
	TABLE_HEADINGS,
	sortBills,
	DEFAULT_SORT_PARAMS,
	COLUMN_SORT_PRIORITY,
	TableHeadings,
	ColumnOrder,
	BillDataWithElements,
} from './source';

export interface BillTrackerTableParams {}

export interface BillTrackerTableState {
	activeSort?: SortParam[];
	billsInView?: BillDataWithElements[];
	visible: boolean;
}

export interface SortParam {
	column: keyof BillData;
	order: ColumnOrder;
}

class BillTrackerTable extends Component<HTMLElement> {
	static selector = '.bill-tracker__table';

	params: BillTrackerTableParams;
	table: HTMLTableElement;
	columnHeaderToggles: HTMLButtonElement[];
	bills: BillData[] = [];
	currentFilter: (bills: BillData) => boolean = (bill: BillData) => true;

	state: BillTrackerTableState = {
		activeSort: [],
		billsInView: [],
		visible: false,
	};

	constructor(element: HTMLElement, params: BillTrackerTableParams) {
		super(element);
		this.params = params;

		const tableContainer = Object.assign(document.createElement('div'), {
			className: 'bill-tracker__table-container',
		});

		// Render essential children
		this.table = createElement<HTMLTableElement>('table', {
			children: [
				createElement('thead', {
					children: [
						createElement('tr', {
							children: Object.entries(TABLE_HEADINGS).map(
								([column, label]) =>
									this.renderColumnHeader(
										column as keyof BillData,
										label
									)
							),
						}),
					],
				}),
				createElement('tbody', {}),
			],
		});

		this.columnHeaderToggles = Array.from(
			this.table.querySelectorAll('th button')
		);

		tableContainer.appendChild(this.table);
		this.element.appendChild(tableContainer);
	}

	async afterInit() {
		this.bills = (await billsData()).bills;
		this.initializeSorting();

		// Initial sorting
		this.sortTable(DEFAULT_SORT_PARAMS);

		this.emit('init', null);
	}

	renderColumnHeader(column: TableHeadings, label: string) {
		const headingTag = COLUMN_SORT_PRIORITY.includes(column)
			? 'button'
			: 'span';

		return createElement('th', {
			attributes: {
				'data-column': column,
			},
			children: [
				createElement<HTMLSpanElement>('span', {
					children: [
						createElement(headingTag, {
							className: 'bill-tracker__table-columnheader',
							attributes: {
								'data-column': column,
							},
							children: [
								createElement('span', {
									textContent: label,
								}),
								headingTag === 'button'
									? createElement('i', {
											className:
												'bill-tracker__table-sorter',
									  })
									: null,
							].filter(Boolean),
						}),
					],
				}),
			],
		});
	}

	clearRows() {
		this.table.tBodies[0].innerHTML = '';
	}

	renderBills(bills: BillDataWithElements[] = []) {
		const fragment = document.createDocumentFragment();
		bills.forEach((bill) => fragment.appendChild(bill.elements.row));
		this.table.tBodies[0].append(fragment);
		this.emit('render', this);
	}

	initializeSorting() {
		const tableHeadingToggles = this.table.querySelectorAll('th button');

		tableHeadingToggles.forEach((button) =>
			button.addEventListener(
				'click',
				this.onColumnheaderClick.bind(this)
			)
		);
	}

	onColumnheaderClick(e: Event) {
		e.preventDefault();
		const column = (<HTMLButtonElement>e.currentTarget)?.getAttribute(
			'data-column'
		) as keyof BillData;
		const buttonOrderValue = (<HTMLButtonElement>(
			e.currentTarget
		))?.getAttribute('aria-sort');
		const order = buttonOrderValue
			? buttonOrderValue
			: column === 'action_date'
			? 'ascending'
			: '';

		this.emit('columnsort', {
			column,
			order: order === 'ascending' ? 'descending' : 'ascending',
		});
	}

	sortTable(sortParams: SortParam[] = []) {
		// Filter bills
		const billsInView = this.bills.slice().filter(this.currentFilter);
		const sortedBillsInView = sortBills(billsInView, sortParams);

		this.setState({
			billsInView: sortedBillsInView,
			activeSort: sortParams,
		});
	}

	setFilter(filterFunction?: (bill: BillData) => boolean) {
		this.currentFilter =
			typeof filterFunction === 'function'
				? filterFunction
				: this.currentFilter || (() => true);

		this.sortTable(this.state.activeSort);
	}

	getCSVData() {
		const bills: BillData[] = this.state.billsInView || [];

		return [
			Object.values(TABLE_HEADINGS).join(','),
			...bills.map((bill) =>
				(Object.keys(TABLE_HEADINGS) as string[])
					.map(
						(prop: string) =>
							`"${String(bill[prop as keyof BillData]).trim()}"`
					)
					.join(',')
			),
		].join('\n');
	}

	static onBillNameClick(e: PointerEvent) {
		e.preventDefault();

		const url = (e.currentTarget as HTMLButtonElement).getAttribute(
			'data-url'
		);

		if (url) {
			window.open(url, '_blank');
		}
	}

	static renderBillValue(billProp: keyof BillData, bill: BillData) {
		switch (billProp) {
			case 'title':
				return createElement('div', {
					children: [
						createElement('button', {
							children: [
								createElement('span', {
									textContent: bill.title,
								}),
							],
							attributes: {
								'data-url': bill.source,
								type: 'button',
							},
							events: {
								click: this.onBillNameClick.bind(
									this
								) as EventListener,
							},
						}),
					],
				});
			case 'statusCategory':
				return createElement('div', {
					innerHTML: `${
						bill.statusCategory
					}<span class="bill-tracker__table-status-indicator" data-status="${bill.statusCategory?.toLowerCase()}"></span>`,
				});
			default:
				return bill[billProp] as string;
		}
	}

	static generateBillRowElement(bill: BillData) {
		return createElement<HTMLTableRowElement>('tr', {
			children: Object.keys(TABLE_HEADINGS).map((billProp) =>
				createElement('td', {
					attributes: {
						'data-column': billProp,
					},
					children: [
						BillTrackerTable.renderBillValue(
							billProp as keyof BillData,
							bill
						),
					],
				})
			),
		});
	}

	// Re-render the table
	componentDidUpdate() {
		const { activeSort = [], billsInView = [], visible } = this.state || {};

		// Update column headers
		this.columnHeaderToggles.forEach((button) => {
			const matchingSortParam = activeSort.find(
				({ column }) => column === button.dataset.column
			);

			if (matchingSortParam) {
				button.setAttribute('aria-sort', matchingSortParam.order);
			} else {
				button.removeAttribute('aria-sort');
			}
		});

		// Render bills
		if (visible) {
			this.clearRows();
			this.renderBills(billsInView);
		}
	}
}

export default BillTrackerTable;
